// This file was based off of the Radix UI CollapsibleContentImpl component: https://github.com/radix-ui/primitives/blob/main/packages/react/collapsible/src/CollapsibleContentImpl.tsx

import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Primitive } from '@radix-ui/react-primitive';
import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '@air/utils-tailwind';

import {
  PANEL_CONTENT_NAME,
  type ScopeProps,
  usePanelContext,
} from '~/components/v2/components/shared/panel/PanelProvider';
import { getPanelState } from '~/components/v2/components/shared/panel/utils/getPanelState';

export type PanelContentImplElement = ComponentRef<typeof Primitive.div>;

export type PanelContentImplProps = ComponentPropsWithoutRef<typeof Primitive.div> & {
  present: boolean;
};

export const PanelContentImpl = forwardRef<PanelContentImplElement, PanelContentImplProps>(
  (
    { __scopePanel, className, children, present, style, ...restOfProps }: ScopeProps<PanelContentImplProps>,
    forwardedRef,
  ) => {
    const context = usePanelContext(PANEL_CONTENT_NAME, __scopePanel);
    const [isPresent, setIsPresent] = useState(present);
    const ref = useRef<PanelContentImplElement>(null);
    const composedRefs = useComposedRefs(forwardedRef, ref);
    const heightRef = useRef<number | undefined>(0);
    const height = heightRef.current;
    const widthRef = useRef<number | undefined>(0);
    const width = widthRef.current;

    // When opening we want it to immediately open to retrieve dimensions
    // When closing we delay `present` to retrieve dimensions before closing
    const isOpen = context.open || isPresent;
    const isMountAnimationPreventedRef = useRef(isOpen);
    const originalStylesRef = useRef<Record<string, string> | undefined>(undefined);

    useEffect(() => {
      const rAF = requestAnimationFrame(() => {
        isMountAnimationPreventedRef.current = false;
      });

      return () => cancelAnimationFrame(rAF);
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies(context.open): depends on `context.open` because it will change to `false` when a close is triggered but `present` will be `false` on animation end (so when close finishes). This allows us to retrieve the dimensions *before* closing
    useLayoutEffect(() => {
      const node = ref.current;
      if (node) {
        originalStylesRef.current = originalStylesRef.current || {
          transitionDuration: node.style.transitionDuration,
          animationName: node.style.animationName,
        };

        // Block any animations/transitions so the element renders at its full dimensions
        node.style.transitionDuration = '0s';
        node.style.animationName = 'none';

        // Get width and height from full dimensions
        const rect = node.getBoundingClientRect();
        heightRef.current = rect.height;
        widthRef.current = rect.width;

        // Kick off any animations/transitions that were originally set up if it isn't the initial mount
        if (!isMountAnimationPreventedRef.current) {
          node.style.transitionDuration = originalStylesRef.current.transitionDuration;
          node.style.animationName = originalStylesRef.current.animationName;
        }

        setIsPresent(present);
      }
    }, [context.open, present]);

    return (
      <Primitive.div
        data-state={getPanelState(context.open)}
        id={context.contentId}
        // Adding `!hidden` to the className enables us to use `flex` without causing the content to be visible when closed.
        className={cn(!isOpen && '!hidden', className)}
        hidden={!isOpen}
        {...restOfProps}
        ref={composedRefs}
        style={{
          // @ts-expect-error - CSS variables are not currently supported by TypeScript
          '--radix-panel-content-height': height ? `${height}px` : undefined,
          '--radix-panel-content-width': width ? `${width}px` : undefined,
          ...style,
        }}
      >
        {isOpen && children}
      </Primitive.div>
    );
  },
);
